import React, { Component } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import { Grid, Typography, IconButton, Tooltip } from '@mui/material';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import PlaceholderJson from '../../Component/PlaceholderJson';
import GenerateToken from '../../Component/GenerateToken';
import useProjectStore from '../../ProjectCanvas/useProjectStore';
import config from '../../../config';

class Summary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasToken: this.props.hasToken,
      tokenRegenerated: false,
      copied: false,
    };
  }

  changeTokenStatus = (status, wasRegenerated = false) => {
    this.setState({ hasToken: status });
    this.props.changeTokenStatus(status);
    
    if (wasRegenerated && status) {
      this.setState({ tokenRegenerated: true });
    }
  };

  handleCopyUrl = () => {
    navigator.clipboard.writeText(`${config.WEBHOOK_ENDPOINT.PRODUCTION}/${this.props.component_id}`);
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  render() {
    const { data } = this.props.componentStore;
    const { hasToken, tokenRegenerated, copied } = this.state;
    const isPublished = useProjectStore.getState().publishedVersion && useProjectStore.getState().publishedVersion > 0;

    return (
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {/* Status Banner */}
          <Grid item xs={12}>
            {tokenRegenerated ? (
              <div className="webhook-status-banner warning">
                <WarningAmberIcon className="status-banner-icon" />
                <div className="status-banner-content">
                  <span className="status-banner-title">Token Regenerated - Republish Required</span>
                  <span className="status-banner-subtitle">
                    You have regenerated the security token. The webhook will not accept requests with the new token until the workflow is republished.
                  </span>
                </div>
              </div>
            ) : isPublished && hasToken ? (
              <div className="webhook-status-banner active">
                <CheckCircleIcon className="status-banner-icon" />
                <div className="status-banner-content">
                  <span className="status-banner-title">Webhook Active</span>
                  <span className="status-banner-subtitle">
                    This webhook endpoint is live and accepting incoming requests
                  </span>
                </div>
              </div>
            ) : (
              <div className="webhook-status-banner inactive">
                <ErrorIcon className="status-banner-icon" />
                <div className="status-banner-content">
                  <span className="status-banner-title">Webhook Inactive</span>
                  <span className="status-banner-subtitle">
                    {!hasToken
                      ? 'Generate a security token and publish the workflow to activate this webhook'
                      : 'Publish the workflow to activate this webhook endpoint'}
                  </span>
                </div>
              </div>
            )}
          </Grid>

          {/* Production Endpoint Section */}
          <Grid item xs={12} className="webhook-section">
            <Typography className="webhook-section-label">Production Endpoint URL</Typography>
            <Typography className="webhook-section-helper">
              Direct your API to send payloads to this URL. Include the security token in the request header.
            </Typography>
            <TextField
              disabled={true}
              value={`${config.WEBHOOK_ENDPOINT.PRODUCTION}/${this.props.component_id}`}
              variant="outlined"
              fullWidth
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                      <IconButton onClick={this.handleCopyUrl} edge="end" size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                sx: { fontFamily: 'monospace', fontSize: '13px' }
              }}
            />
          </Grid>

          {/* Security Token Section */}
          <Grid item xs={12} className="webhook-section">
            <Typography className="webhook-section-label">Security Token</Typography>
            <Typography className="webhook-section-helper">
              Include this token in the request header (recommended) or as a URL parameter.{' '}
              <a
                href="https://docs.workflow86.com/docs/components/webhook_import/#how-to-set-the-token"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'none' }}
              >
                Learn more
              </a>
            </Typography>

            <GenerateToken
              hasToken={status => {
                const wasRegenerated = this.state.hasToken && status;
                this.changeTokenStatus(status, wasRegenerated);
              }}
              tokenStatus={this.props.hasToken}
              name={this.props.ComponentName}
              component_id={this.props.component_id}
            />

            {/* Token Status Messages */}
            {tokenRegenerated && (
              <div className="webhook-token-warning">
                <WarningAmberIcon className="warning-icon" />
                <div className="warning-content">
                  <span className="warning-title">Action Required: Republish Workflow</span>
                  <span className="warning-text">
                    You have generated a new token. The webhook will reject requests using the old token. 
                    Republish this workflow and update your external system with the new token.
                  </span>
                </div>
              </div>
            )}

            {!hasToken && (
              <div className="webhook-info-box">
                <InfoOutlinedIcon className="info-icon" />
                <span className="info-text">
                  A security token is required to protect your webhook endpoint from unauthorized requests.
                </span>
              </div>
            )}

            {hasToken && !tokenRegenerated && (
              <div className="webhook-info-box">
                <InfoOutlinedIcon className="info-icon" />
                <span className="info-text">
                  A token has been generated. If you've lost it, you can regenerate a new one - but remember to update your external system and republish the workflow.
                </span>
              </div>
            )}
          </Grid>

          {/* Placeholders Section */}
          <Grid item xs={12} className="webhook-section">
            <Typography className="webhook-section-label">
              Output Placeholders{' '}
              <span
                onClick={() => this.props.onChangeState(3, 3)}
                style={{ 
                  color: '#1976d2', 
                  fontWeight: 'normal', 
                  cursor: 'pointer',
                  fontSize: '13px',
                  marginLeft: '8px'
                }}
              >
                Edit
              </span>
            </Typography>
            <Typography className="webhook-section-helper">
              The following placeholders will be generated from the incoming data
            </Typography>
            <PlaceholderJson
              componentStore={this.props.componentStore}
              data={this.props.componentStore.data}
              stepStatus={this.props.stepStatus}
              placeholderJson={data.samplePlaceholders}
            />
          </Grid>

          {/* Back Button */}
          <Grid item xs={12}>
            <Button
              onClick={() => this.props.onChangeState(5, 5)}
              variant="outlined"
              color="info"
            >
              Back
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

export default Summary;
